import I18n from 'components/I18n'
import { actions, selectors } from 'data'
import { TwoFAType } from 'data/types'
import messages from 'lang'
import React, { useEffect } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { color, fontSize } from 'utils'

import { Input as GeneralInput, InputContainer, Label as GeneralLabel } from './styles'

const Input = styled(GeneralInput)`
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;
`

const Label = styled(GeneralLabel)`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`

const Link = styled.span`
  color: ${color('blue600')};
  font-size: ${fontSize('sm')};
  margin-top: 0.5rem;
  cursor: pointer;
`

type Props = {
  className?: string
  onChange: (secondFactor: string) => void
  value: string
}

const getTwoFactorLabel = (authType: TwoFAType | string) => {
  switch (authType) {
    case 'TOTP':
      return {
        label: messages.label_two_fa_withdrawal_two_step_code,
        placeholder: messages.label_two_fa_withdrawal_six_digit_code_placeholder
      }

    case 'WALLET_GOOGLE_AUTH':
      return {
        label: messages.label_two_fa_withdrawal_two_step_code,
        placeholder: messages.label_two_fa_withdrawal_six_digit_code_placeholder
      }

    case 'WALLET_SMS':
      return {
        label: messages.label_two_fa_withdrawal_sms_code,
        placeholder: messages.label_two_fa_withdrawal_sms_code_placeholder
      }

    case 'WALLET_YUBIKEY':
      return {
        label: messages.label_two_fa_withdrawal_two_step_code,
        placeholder: messages.label_two_fa_withdrawal_yubikey_placeholder
      }

    default:
      return {
        label: messages.label_two_fa_withdrawal_two_step_code,
        placeholder: messages.label_two_fa_withdrawal_six_digit_code_placeholder
      }
  }
}

const SecondFactorInput = ({ className, intl, onChange, value }: Props & WrappedComponentProps) => {
  const twoFactorStatus = useSelector(selectors.auth.getTwoFactorStatus)
  const has2FASetup = twoFactorStatus && twoFactorStatus.length > 0
  const needsChallenge = has2FASetup && twoFactorStatus!.includes('WALLET_SMS')
  const dispatch = useDispatch()
  const authDesc = getTwoFactorLabel(
    twoFactorStatus && twoFactorStatus.length > 0 ? twoFactorStatus[0] : ''
  )
  const sendSMS = () => dispatch(actions.auth.secondFactorChallenge.createChallenge())

  useEffect(() => {
    if (needsChallenge) {
      sendSMS()
    }
  }, [needsChallenge])

  if (!twoFactorStatus) return null

  return (
    <InputContainer className={className}>
      <Label htmlFor='twoFactorCode'>
        <span>
          <I18n data={authDesc.label} />
        </span>
      </Label>
      <Input
        data-e2e='withdrawal2faInput'
        name='twoFactorCode'
        onChange={(e) => onChange(e.target.value)}
        placeholder={intl.formatMessage(authDesc.placeholder)}
        value={value}
        type={twoFactorStatus.includes('WALLET_YUBIKEY') ? 'password' : 'text'}
      />
      {needsChallenge && (
        <Link onClick={sendSMS}>
          <I18n data={messages.resend_code} />
        </Link>
      )}
    </InputContainer>
  )
}

export default injectIntl(SecondFactorInput)
